import { observable, action, computed } from 'mobx'
import { has, last } from 'lodash'

import BaseListItem, {
  IBaseListItemUpdateOptions,
} from 'stores/base/BaseListItem'
import ModelStore from 'stores/model/ModelStore'
import Model from 'stores/model/Model'
import FieldStore from 'stores/field/FieldStore'
import Field from 'stores/field/Field'
import RecordStore from 'stores/record/RecordStore'
import Record from 'stores/record/Record'
import TabStore from 'stores/tab/TabStore'
import ActionStore from 'stores/action/ActionStore'
import LayoutStore from 'stores/layout/LayoutStore'
import ErrorStore from 'stores/error/ErrorStore'
import ProjectStore from 'stores/project/ProjectStore'
import { IScriptSchema } from 'stores/persistence/types'
import ScriptWhere, {
  IScriptWhereCreatePayload,
  IScriptWhereUpdatePayload,
} from './ScriptWhere'
import ScriptPagination, {
  DEFAULT_PAGE_SIZE,
  IScriptPagination,
} from './ScriptPagination'
import ScriptSort, { IScriptSort } from './ScriptSort'

import getFindManyQuery from 'utils/getFindManyQuery'
import getScriptHash from 'utils/getScriptHash'
import runQuery from 'utils/runQuery'
import createError from 'utils/createError'
import dedup from 'utils/dedup'

export type IInputMode = 'visual' | 'code'
export type IViewMode = 'table' | 'tree'

export interface IScriptCreatePayload {
  id: string
  frozen: boolean
  name: string | null
  code?: string

  modelId: string
  fieldIds: string[]

  where?: IScriptWhereCreatePayload[]
  sort?: IScriptSort
  pagination?: Partial<IScriptPagination>
}

type IScriptUpdatePayload = Partial<IScriptCreatePayload> &
  Partial<{
    inputMode: IInputMode
    where: IScriptWhereUpdatePayload[]
    sort: Partial<IScriptSort>
    pagination: Partial<IScriptPagination>
    viewMode: IViewMode
    code: string

    recordIds: string[]
    running: boolean
  }>

class Script extends BaseListItem {
  @observable id: IScriptCreatePayload['id']
  @observable frozen: IScriptCreatePayload['frozen']
  @observable name: IScriptCreatePayload['name']
  @observable modelId: IScriptCreatePayload['modelId']
  @observable fieldIds: IScriptCreatePayload['fieldIds']

  @observable inputMode: IInputMode
  @observable where: ScriptWhere
  @observable sort: ScriptSort
  @observable pagination: ScriptPagination
  @observable viewMode: IViewMode
  @observable code: string

  @observable running: boolean

  @observable private __recordIds: string[] // Actual recordIds according to the database (aka excluding pending actions)
  // Use computed prop `recordIds` everywhere else to account for pending actions

  constructor(script: IScriptCreatePayload) {
    super(script)

    this.id = script.id
    this.frozen = script.frozen
    this.name = script.name
    this.modelId = script.modelId
    this.fieldIds = script.fieldIds || []

    this.inputMode = 'visual'
    this.where = new ScriptWhere({ modelId: script.modelId, scriptId: this.id })
    ;(script.where || []).forEach(w => this.where.add(w))
    this.sort = new ScriptSort({
      fieldId: script.sort?.fieldId ?? null,
      order: script.sort?.order ?? 'asc',
    })
    this.pagination = new ScriptPagination({
      take: script.pagination?.take ?? DEFAULT_PAGE_SIZE,
      skip: script.pagination?.skip ?? 0,
    })
    this.viewMode = 'table'
    this.code =
      script.code ||
      getFindManyQuery({
        modelId: this.modelId,
        where: this.where,
        // Fetch all fields always, we'll filter what we show
        // fieldIds: this.fieldIds,
        sort: this.sort,
        pagination: this.pagination,
      })

    this.__recordIds = []
    this.running = false
  }

  @computed
  get hash(): string {
    return getScriptHash({
      code: this.code,
      modelId: this.modelId,
      where: this.where,
      fieldIds: this.fieldIds,
    })
  }

  @computed
  get model(): Model {
    let model = ModelStore.get(this.modelId)
    if (!model) {
      throw createError('Script.model', 'Invalid modelId', {
        modelId: this.modelId,
      })
    }
    return model
  }

  @computed
  get fields(): Field[] {
    return this.fieldIds.map(fId => {
      const field = FieldStore.get(fId)
      if (!field) {
        throw createError('Script.fields', 'Invalid fieldId', {
          fieldId: fId,
        })
      }
      return field
    })
  }

  @computed
  get recordIds(): string[] {
    const createdRecordIds = ActionStore.actions
      .filter(
        a =>
          a.type === 'create' &&
          a.sessionId === TabStore.activeTab?.sessionId &&
          a.record?.modelId === this.modelId
      )
      .map(a => a.recordId)
      .filter((rId): rId is string => !!rId)

    const deletedRecordIds = ActionStore.actions
      .filter(
        a =>
          a.type === 'delete' && a.sessionId === TabStore.activeTab?.sessionId
      )
      .map(a => a.recordId)

    return [
      ...createdRecordIds, // Add created recordIds
      ...this.__recordIds.filter(
        rId => !deletedRecordIds.includes(rId) // Remove deleted records
      ),
    ]
  }

  @computed
  get records(): Record[] {
    return this.recordIds
      .map(rId => RecordStore.get(rId))
      .filter((r): r is Record => !!r)
  }

  @computed
  get uncommittedRecords(): Record[] {
    return ActionStore.actions
      .filter(a => a.type === 'create' && a.record?.modelId === this.modelId)
      .map(a => a.record)
      .filter((r): r is Record => !!r)
  }

  /**
   * Runs this script.
   * This is (should) only be called once per script. loadMore will be called to load more records
   */
  @action
  run = async () => {
    const model = this.model
    if (!model) {
      throw createError('Script.run', 'Invalid modelId', {
        modelId: this.modelId,
      })
    }

    this.update({ running: true })
    LayoutStore.updateError({ visible: false })

    const code =
      this.frozen || this.inputMode === 'visual'
        ? getFindManyQuery({
            modelId: this.modelId,
            where: this.where,
            // Fetch all fields always, we'll filter what we show
            // fieldIds: this.fieldIds,
            sort: this.sort,
            pagination: this.pagination,
          })
        : this.code

    try {
      // Always count the number of records in this table so it is up-to-date
      this.model.runCountQuery()

      // Then, run the `code`
      const { recordIds, modelId, fieldIds } = await runQuery(code)

      this.update({ recordIds })

      if (this.inputMode === 'visual') {
        this.update({ code })
      }
      if (this.inputMode === 'code') {
        this.update({ modelId, fieldIds })
      }
    } catch (e) {
      ErrorStore.update({
        type: 'client',
        description: 'Unable to run script',
        dump: `Message: ${e.message}

Query:
${code}
`,
      })
      LayoutStore.updateError({ visible: true })
    } finally {
      this.update({
        running: false,
      })
    }
  }

  @action
  loadMore = async (): Promise<Record[]> => {
    // We can update whatever we want in here because this will only be called when the user scrolls
    // This means overwriting `take` & `skip` is okay here (without worrying obout what the user might have in there already)
    if (
      this.__recordIds.length >= (this.model.count || 0) || // should never happen
      this.inputMode === 'code' // should never be called from code mode
    ) {
      return []
    }

    this.update({ running: true })

    const code = getFindManyQuery({
      modelId: this.modelId,
      where: this.where,
      // Fetch all fields always, we'll filter what we show
      // fieldIds: this.fieldIds,
      sort: this.sort,
      pagination: {
        take: DEFAULT_PAGE_SIZE,
        skip: this.pagination.skip + this.__recordIds.length,
      },
    })

    try {
      const { recordIds: moreRecordIds } = await runQuery(code)
      const recordIds = dedup([...this.__recordIds, ...moreRecordIds])

      this.update({
        running: false,
        recordIds,
        pagination: {
          take: recordIds.length, // Sync back `take` to reflect the number of records fetched
        },
      })

      return moreRecordIds
        .map(rId => RecordStore.get(rId))
        .filter((r): r is Record => !!r)
    } catch (e) {
      this.update({ running: false })
      ErrorStore.update({
        type: 'client',
        description: 'Unable to fetch paginated records',
        dump: `Message: ${e.message}
                
Query:
${code}
`,
      })
      LayoutStore.updateError({ visible: true })
      throw createError(
        'Script.loadMore',
        'Unable to fetch next page of records',
        {
          take: this.pagination.take,
          skip: this.pagination.skip,
          lastFetchedRecordId: last(this.__recordIds),
        },
        e
      )
    }
  }

  @action
  update = (
    script: IScriptUpdatePayload,
    options: IBaseListItemUpdateOptions = {}
  ) => {
    has(script, 'name') && (this.name = script.name!)
    has(script, 'frozen') && (this.frozen = script.frozen!)
    has(script, 'modelId') && (this.modelId = script.modelId!)
    has(script, 'fieldIds') && (this.fieldIds = script.fieldIds as string[])

    has(script, 'inputMode') && (this.inputMode = script.inputMode!)
    has(script, 'viewMode') && (this.viewMode = script.viewMode!)
    has(script, 'sort') && this.sort.update(script.sort!)
    has(script, 'pagination') && this.pagination.update(script.pagination!)
    has(script, 'code') && (this.code = script.code!)

    has(script, 'recordIds') &&
      (this.__recordIds = (script.recordIds as string[]).filter(
        rId => RecordStore.get(rId)?.modelId === this.modelId
      ))

    has(script, 'running') && (this.running = script.running!)

    super.update(script, options)
  }

  @action
  reset() {
    this.update({
      recordIds: [],
      inputMode: 'visual',
      viewMode: 'table',
      running: false,
    })
    this.sort.reset()
    this.pagination.reset()
  }

  serialize = (): IScriptSchema => {
    return {
      projectId: ProjectStore.activeProjectId!,

      id: this.id,
      frozen: this.frozen,
      name: this.name,

      inputMode: this.inputMode,
      code: this.code,
      modelId: this.modelId,
      where: this.where.serialize(),
      fieldIds: this.fieldIds.map(fId => String(fId)),
      sortFieldId: this.sort.fieldId,
      sortOrder: this.sort.order,
      viewMode: this.viewMode,
    }
  }
}

export default Script
